

// export default LeaveApplication;
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import "../assets/css/leave.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";

const LeaveApplication = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);

  const [formData, setFormData] = useState({
    employeeId: 56,
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    adminId: 14,
    adminRemark: "",
  });

  const [leaveData, setLeaveData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  // Pagination states
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profile?.id) fetchLeaves();
  }, [profile]);

  const fetchLeaves = async () => {
    try {
      const response = await axios.get(`https://narrowitsolution.in/api/history/${profile.id}`);
      setLeaveData(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load leave data.");
    }
  };

  const convertToDDMMYYYY = (dateStr) => {
    const parts = dateStr.split("-");
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { leaveType, startDate, endDate, reason, adminRemark } = formData;

    if (!leaveType || !startDate || !endDate || !reason) {
      toast.error("All fields are required.");
      return;
    }

    const payload = {
      employeeId: profile.id,
      adminId: profile.superAdminId,
      leaveType,
      startDate: convertToDDMMYYYY(startDate),
      endDate: convertToDDMMYYYY(endDate),
      reason,
      adminRemark,
    };

    try {
      setLoading(true);
      await axios.post("https://narrowitsolution.in/api/leave-apply", payload);

      toast.success("Leave application submitted!");

      setFormData({
        employeeId: 0,
        leaveType: "",
        startDate: "",
        endDate: "",
        reason: "",
        adminId: 0,
        adminRemark: "",
      });

      fetchLeaves();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to submit leave.");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 Filter data
  const filteredLeaves = leaveData.filter((leave) =>
    leave.employeeId?.toString().includes(search)
  );

  // 📌 Pagination Logic
  const totalPages = Math.ceil(filteredLeaves.length / rowsPerPage);

  const paginatedLeaves = filteredLeaves.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="leave-wrapper">

      {/* 🌟 Apply Leave Form */}
      <section className="leave-section leave-form-section">
        <h2>Apply for Leave</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">

            <div className="leave-form-group">
              <label>Leave Type</label>
              <select
                name="leaveType"
                value={formData.leaveType}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                <option value="Full Day">Full Day</option>
                <option value="Half Day">Half Day</option>
                <option value="Short Leave">Short Leave</option>
              </select>
            </div>

            <div className="leave-form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                min={today}
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>

            <div className="leave-form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                min={formData.startDate || today}
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>

            <div className="leave-form-group">
              <label>Reason</label>
              <textarea
                name="reason"
                placeholder="Enter reason for leave"
                value={formData.reason}
                onChange={handleChange}
              ></textarea>
            </div>
          </div>

          <button type="submit" className="leave-submit-button" disabled={loading}>
            {loading ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </section>

      {/* 📊 Leave Table */}
      <section className="leave-section leave-table-section">
        <h2>Leave Report</h2>

        <input
          type="text"
          placeholder="Search by Employee ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="leave-search"
        />

        <div className="table-responsive">
          <table className="leave-table">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>Start</th>
                <th>End</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {paginatedLeaves.length > 0 ? (
                paginatedLeaves.map((leave) => (
                  <tr key={leave.id}>
                    <td>{leave.leaveType}</td>
                    <td>{new Date(leave.startDate).toLocaleDateString("en-GB").replaceAll("/","-")}</td>
                    <td>{new Date(leave.endDate).toLocaleDateString("en-GB").replaceAll("/","-")}</td>
                    <td>{leave.reason}</td>
                    <td>
                      <span className={`leave-status-badge ${leave.status}`}>
                        {leave.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">No leave applications found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 📌 Pagination UI */}
        <div className="pagination-container">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ◀ Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              className={`pagination-number ${
                currentPage === i + 1 ? "active" : ""
              }`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next ▶
          </button>
        </div>
      </section>
    </div>
  );
};

export default LeaveApplication;

